// Global Types
import type {
  $FullAdminRegatta,
  $Model,
  $ModelUpsertParams,
} from 'common.data';
import type {
  $Language,
  $Translate,
} from 'common.language';

// Global Enums
import {
  RegattaDocumentStatus,
} from 'common.data';
import {
  docRoute,
  docType,
} from 'common.enums';

// Global Components
import * as React from 'react';
import {
  Button,
  Card,
  Table,
} from 'reactstrap';
import {
  DeleteDialog,
  FormDialog,
  Loader,
  SectionHeader,
  TdDate,
} from '@vroskus/library-components';

// Helpers
import {
  cleanFormValues,
  getFileUrl,
  isDiff,
} from '@vroskus/library-helpers/dist/browser';
import {
  i18next,
  regattaDocumentStatusLabel,
} from 'common.language';
import {
  defaultDeleteItem,
  defaultGetData,
  defaultUpsertItem,
  getDocumentUrl,
  publicBorder,
} from '../../../../helpers';
import config from '../../../../config';

// Components
import EmptyTable from '../../../common/EmptyTable';
import RegattaDocumentFields from '../../../fields/RegattaDocumentFields';
import ThQty from '../../../common/ThQty';

// Services
import Services from '../../../../services';

// Enums
import {
  regattaDocumentStatusClass,
} from '../../../../enums';

// Types
type $Props = $Translate & {
  id: string;
  item: $FullAdminRegatta;
};

type $State = {
  data: Array<$Model<'RegattaDocument'>>;
  loading: boolean;
};

class RegattaDocumentsSectionView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      data: [],
      loading: false,
    };
  }

  componentDidMount(): void {
    this.getData();
    this.setupSocket();
  }

  componentDidUpdate(prevProps: $Props): void {
    const {
      item,
    } = this.props;

    if (isDiff(
      item.id,
      prevProps.item.id,
    )) {
      this.getData();
    }
  }

  setupSocket(): void {
    Services.Socket.on({
      action: (data) => {
        const {
          item,
        } = this.props;

        if (item.id === data.RegattaId && data.modelName === 'RegattaDocument') {
          this.getData();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  setStateAsync(state: Partial<$State>): Promise<void> {
    return new Promise((resolve) => {
      this.setState(
        (pervState: $State) => ({
          ...pervState,
          ...state,
        }),
        resolve,
      );
    });
  }

  async getData(): Promise<void> {
    const {
      item,
    } = this.props;

    const RegattaId = item.id;

    await defaultGetData({
      action: async () => {
        await this.setStateAsync({
          loading: true,
        });

        const data = await Services.Api.getManyRegattaDocument({
          RegattaId,
        });

        await this.setStateAsync({
          data,
          loading: false,
        });
      },
      errorAction: async () => this.setStateAsync({
        loading: false,
      }),
    });
  }

  renderHeader() {
    const {
      item,
      t,
    } = this.props;

    const RegattaId = item.id;

    const newItem: $ModelUpsertParams<'RegattaDocument'> = {
      Content: null,
      DocumentDate: new Date(),
      File: null,
      Name: '',
      Public: true,
      PublishDate: null,
      RegattaId,
      Status: RegattaDocumentStatus.pending,
      StatusContent: null,
    };

    return (
      <SectionHeader
        bottomBorder
        description={'A list of regatta documents that can be either PDF files or free form text'}
        iconClass={'icon-docs'}
        rightButtons={(
          <FormDialog
            fields={(
              <RegattaDocumentFields item={newItem} />
            )}
            id={'create-regatta-document-dialog'}
            labelCloseButton={t('Close')}
            labelSaveButton={t('Save')}
            onAction={(values) => defaultUpsertItem({
              action: async () => {
                const params = cleanFormValues<$Model<'RegattaDocument'>>({
                  ...newItem,
                  ...values,
                });

                await Services.Api.upsertRegattaDocument(params);
              },
            })}
            subTitle={t('New')}
            title={t('Regatta document')}
          >
            <Button
              color={'info'}
              id={'create-regatta-document-button'}
              size={'sm'}
              title={t('Add new document')}
            >
              {t('Add new')}
            </Button>
          </FormDialog>
        )}
        title={t('Regatta documents')}
      />
    );
  }

  renderButtons(dataItem: $Model<'RegattaDocument'>) {
    const {
      t,
    } = this.props;

    const {
      id,
    } = dataItem;

    return (
      <div className={'d-flex justify-content-end'}>
        <Button
          className={'mr-1'}
          color={'secondary'}
          href={dataItem.File ? getFileUrl({
            cdnUrl: config.CDN_URL,
            file: dataItem.File,
          }) : getDocumentUrl({
            cdnUrl: config.CDN_URL,
            docRoute: docRoute.adminRegattaDocument,
            docType: docType.pdf,
            id,
            language: i18next.language as $Language,
          })}
          id={`regatta-document-file-${dataItem.id}`}
          outline
          size={'sm'}
          target={'_blank'}
        >
          <em className={'far fa-file-pdf'} />
        </Button>
        <FormDialog
          fields={(
            <RegattaDocumentFields item={dataItem} />
          )}
          id={`update-regatta-document-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelSaveButton={t('Save')}
          onAction={(values) => defaultUpsertItem({
            action: async () => {
              const params = cleanFormValues<$Model<'RegattaDocument'>>({
                ...dataItem,
                ...values,
              });

              await Services.Api.upsertRegattaDocument(params);
            },
          })}
          subTitle={dataItem.Name}
          title={t('Regatta document')}
        >
          <Button
            className={'mr-1'}
            color={'warning'}
            id={`update-regatta-document-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Edit')}
          </Button>
        </FormDialog>
        <DeleteDialog
          content={t('Do you really want to delete?')}
          id={`delete-regatta-document-dialog-${id}`}
          labelCloseButton={t('Close')}
          labelDeleteButton={t('Delete')}
          onAction={() => defaultDeleteItem({
            action: async () => Services.Api.deleteRegattaDocument(dataItem),
          })}
          subTitle={dataItem.Name}
          title={t('Regatta document')}
        >
          <Button
            color={'danger'}
            id={`delete-regatta-document-button-${id}`}
            outline
            size={'sm'}
          >
            {t('Delete')}
          </Button>
        </DeleteDialog>
      </div>
    );
  }

  renderTable() {
    const {
      t,
    } = this.props;
    const {
      data,
    } = this.state;

    return data.length === 0 ? (
      <EmptyTable />
    ) : (
      <Table responsive>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Document date')}</th>
            <th>{t('Publish date')}</th>
            <th>{t('Status')}</th>
            <th>{t('Remarks')}</th>
            <ThQty value={data.length} />
          </tr>
        </thead>
        <tbody>
          {data.map((dataItem: $Model<'RegattaDocument'>) => (
            <tr
              key={dataItem.id}
              style={publicBorder(dataItem.Public)}
            >
              <td>{dataItem.Name}</td>
              <TdDate value={dataItem.DocumentDate} />
              <TdDate
                duration
                multiple
                value={dataItem.PublishDate}
              />
              <td>
                <span className={`badge rounded-pill ${regattaDocumentStatusClass[dataItem.Status]}`}>
                  {t(regattaDocumentStatusLabel[dataItem.Status])}
                </span>
              </td>
              <td>{dataItem.StatusContent}</td>
              <td>
                {this.renderButtons(dataItem)}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderContent() {
    const {
      loading,
    } = this.state;

    return (
      <Loader
        loading={loading === true}
        overlay
      >
        {this.renderTable()}
      </Loader>
    );
  }

  render() {
    const {
      id,
    } = this.props;

    return (
      <Card id={id}>
        {this.renderHeader()}
        {this.renderContent()}
      </Card>
    );
  }
}

export default RegattaDocumentsSectionView;
